import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useState } from 'react';
import { registerForWebinar } from '../services/apiService';
import { toast } from 'sonner';
import { Calendar, Clock, Video } from 'lucide-react';

export const Hero = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await registerForWebinar(formData);
      toast.success('Success!', {
        description: 'You\'re registered for the webinar. Check your email for details.',
      });
      setFormData({ firstName: '', lastName: '', phone: '', email: '' });
    } catch (error) {
      toast.error('Registration Failed', {
        description: error.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 opacity-5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 opacity-5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 space-y-6">
            <div className="inline-block">
              <span className="text-yellow-400 font-semibold text-sm tracking-wider uppercase border border-yellow-400/30 px-4 py-2 rounded-full">
                [Online] Home Buyers Masterclass
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold leading-tight">
              <span className="text-white">From Confused to Confident:</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-500">
                Your Guide to Buying Your First Home
              </span>
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold">December 17th, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold">7:00 PM EST</span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold">Zoom Meeting</span>
              </div>
            </div>
          </div>

          {/* Two column layout */}
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left: Value proposition */}
            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
                <p className="text-xl text-blue-100 leading-relaxed">
                  It takes the right <span className="text-yellow-400 font-semibold">information</span> and the right mortgage & real estate <span className="text-yellow-400 font-semibold">team</span> to purchase in <span className="text-yellow-400 font-semibold">today's</span> market.
                </p>
              </Card>

              <div className="hidden md:block">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80" 
                  alt="Dream Home" 
                  className="rounded-lg shadow-2xl w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Right: Registration form */}
            <Card className="bg-white text-slate-900 p-8 shadow-2xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Reserve Your Spot</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="firstName" className="text-slate-700">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-slate-300 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <Label htmlFor="lastName" className="text-slate-700">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-slate-300 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-1 border-slate-300 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 border-slate-300 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <p className="text-xs text-slate-600 leading-relaxed">
                  By submitting this form, you agree to receive automated, promotional and personalised marketing communications regarding this event. We do not sell or share your data. Reply Stop to opt-out.
                </p>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold py-6 text-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                >
                  {isSubmitting ? 'Registering...' : 'Register Now'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
